import { HelpCircle, X } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRouterState } from "@tanstack/react-router";
import {
  CircleTourPreview,
  CIRCLE_PREVIEW_STEPS,
} from "@/components/onboarding/CircleTourPreview";
import {
  useConsumerOnboarding,
  useSaveConsumerOnboarding,
} from "@/lib/consumerOnboarding";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

export const REPLAY_TAB_TOUR_EVENT = "pulse:replay-tab-tour";

export function requestTabTourReplay() {
  window.dispatchEvent(new Event(REPLAY_TAB_TOUR_EVENT));
}

const TOUR_TABS = [
  { key: "discover" },
  { key: "aiConcierge" },
  { key: "circle" },
  { key: "tickets" },
  { key: "profile" },
] as const;

type TourTabKey = (typeof TOUR_TABS)[number]["key"];

type SpotlightRect = {
  left: number;
  top: number;
  size: number;
};

function measureTourTab(key: TourTabKey): SpotlightRect | null {
  const tab = document.querySelector<HTMLElement>(`[data-tour-tab="${key}"]`);
  if (!tab) return null;
  // Prefer the glyph — framing the whole tab (icon+label) made the ring sit high
  // and oversized relative to what users expect around the nav icon.
  const icon =
    tab.querySelector<HTMLElement>("svg") ??
    tab.querySelector<HTMLElement>("[aria-hidden]") ??
    tab;
  const rect = icon.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  const size = Math.max(rect.width, rect.height) + 28;
  return {
    left: rect.left + rect.width / 2,
    top: rect.top + rect.height / 2,
    size,
  };
}

type DiscoveryTourProps = {
  forceOpen?: boolean;
  onClose?: () => void;
};

export function DiscoveryTour({ forceOpen = false, onClose }: DiscoveryTourProps) {
  const { t } = useTranslation("consumer");
  const { user, isGuest } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data: onboarding, isSuccess } = useConsumerOnboarding();
  const { save } = useSaveConsumerOnboarding();
  const [index, setIndex] = useState(0);
  const [circlePreviewStep, setCirclePreviewStep] = useState(0);
  const [open, setOpen] = useState(false);
  const [spotlight, setSpotlight] = useState<SpotlightRect | null>(null);

  useEffect(() => {
    const onReplay = () => {
      setIndex(0);
      setCirclePreviewStep(0);
      setOpen(true);
    };
    window.addEventListener(REPLAY_TAB_TOUR_EVENT, onReplay);
    return () => window.removeEventListener(REPLAY_TAB_TOUR_EVENT, onReplay);
  }, []);

  useEffect(() => {
    if (forceOpen) {
      setIndex(0);
      setCirclePreviewStep(0);
      setOpen(true);
      return;
    }
    if (!user || isGuest || !isSuccess || !onboarding) return;
    // Tour content now lives in /welcome; only open for legacy users who
    // finished welcome before tour_completed was set at gate finish.
    if (onboarding.tourCompleted) return;
    if (!onboarding.welcomeCompleted) return;
    if (pathname !== "/" && pathname !== "") return;
    setOpen(true);
  }, [forceOpen, user, isGuest, isSuccess, onboarding, pathname]);

  const close = useCallback(
    async (completed: boolean) => {
      setOpen(false);
      setCirclePreviewStep(0);
      onClose?.();
      if (completed || !forceOpen) {
        try {
          await save({ tourCompleted: true });
        } catch {
          /* ignore */
        }
      }
    },
    [forceOpen, onClose, save],
  );

  const tab = TOUR_TABS[index] ?? TOUR_TABS[0];
  const isCircleStep = tab.key === "circle";

  useEffect(() => {
    if (isCircleStep) setCirclePreviewStep(0);
  }, [isCircleStep, index]);

  useLayoutEffect(() => {
    if (!open) {
      setSpotlight(null);
      return;
    }
    const update = () => setSpotlight(measureTourTab(tab.key));
    update();
    // Double-rAF: TabBar layout can settle after first paint (font/nav padding).
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(update);
    });
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, index, tab.key]);

  const advance = useCallback(() => {
    if (isCircleStep && circlePreviewStep < CIRCLE_PREVIEW_STEPS - 1) {
      setCirclePreviewStep((s) => s + 1);
      return;
    }
    if (index >= TOUR_TABS.length - 1) void close(true);
    else {
      setCirclePreviewStep(0);
      setIndex((i) => i + 1);
    }
  }, [circlePreviewStep, close, index, isCircleStep]);

  if (!open) return null;

  // Circle preview needs more horizontal room — center the card instead of
  // hugging the spotlight, while other steps stay spotlight-aligned.
  const tooltipLeft = isCircleStep
    ? window.innerWidth / 2
    : (spotlight?.left ?? window.innerWidth / 2);
  const tooltipBottom = isCircleStep
    ? Math.max(88, window.innerHeight - (spotlight?.top ?? window.innerHeight) + 28)
    : spotlight
      ? Math.max(16, window.innerHeight - spotlight.top + spotlight.size / 2 + 12)
      : 96;

  const nextLabel = (() => {
    if (isCircleStep && circlePreviewStep < CIRCLE_PREVIEW_STEPS - 1) {
      return t("onboarding.tour.circlePreview.continue");
    }
    if (index >= TOUR_TABS.length - 1) return t("onboarding.tour.done");
    return t("onboarding.tour.next");
  })();

  return (
    <div className="fixed inset-0 z-[80] bg-black/55" role="dialog" aria-modal="true">
      <div
        className={cn(
          "absolute -translate-x-1/2 rounded-2xl border border-white/15 bg-[#141416] p-4 shadow-2xl",
          isCircleStep ? "w-[min(94vw,24rem)]" : "w-[min(92vw,22rem)]",
        )}
        style={{ left: tooltipLeft, bottom: tooltipBottom }}
      >
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm leading-relaxed text-white/85">
            {t(`onboarding.tour.${tab.key}`)}
          </p>
          <button
            type="button"
            aria-label="Close"
            onClick={() => void close(true)}
            className="shrink-0 text-white/40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {isCircleStep ? (
          <div className="mt-3">
            <CircleTourPreview step={circlePreviewStep} />
          </div>
        ) : null}

        <div className="mt-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => void close(true)}
            className="text-xs text-white/45"
          >
            {t("onboarding.tour.skip")}
          </button>
          <button
            type="button"
            onClick={advance}
            className="rounded-full bg-[#D4A84B] px-4 py-2 text-xs font-semibold text-[#0B0B0C]"
          >
            {nextLabel}
          </button>
        </div>
      </div>
      {spotlight ? (
        <div
          className="pointer-events-none absolute rounded-full border-2 border-[#D4A84B] shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]"
          style={{
            left: spotlight.left,
            top: spotlight.top,
            width: spotlight.size,
            height: spotlight.size,
            transform: "translate(-50%, -50%)",
          }}
        />
      ) : null}
    </div>
  );
}

/** Inline Discover-header help control (not a floating FAB). */
export function DiscoverHelpButton({ className }: { className?: string }) {
  const { t } = useTranslation("consumer");
  const [open, setOpen] = useState(false);
  const { user, isGuest } = useAuth();

  if (!user || isGuest) return null;

  return (
    <>
      <button
        type="button"
        aria-label={t("onboarding.help.title")}
        onClick={() => setOpen(true)}
        className={cn(
          "grid h-11 w-11 shrink-0 place-items-center rounded-full border border-border bg-surface text-muted-foreground transition-colors hover:text-foreground",
          className,
        )}
      >
        <HelpCircle className="h-5 w-5" />
      </button>

      {open ? (
        <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/50 p-4 sm:items-center">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#141416] p-5 text-white">
            <div className="flex items-center justify-between">
              <h2 className="font-headline text-lg font-bold">{t("onboarding.help.title")}</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-white/40">
                <X className="h-4 w-4" />
              </button>
            </div>
            <ul className="mt-4 space-y-3 text-sm text-white/70">
              <li>{t("onboarding.help.faqCircle")}</li>
              <li>{t("onboarding.help.faqConcierge")}</li>
              <li>{t("onboarding.help.faqTiers")}</li>
            </ul>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                requestTabTourReplay();
              }}
              className="mt-5 w-full rounded-full border border-[#D4A84B]/40 bg-[#D4A84B]/15 px-4 py-3 text-sm font-semibold text-[#D4A84B]"
            >
              {t("onboarding.help.replayTour")}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
